/**
 *
 * This service is a **helper** to storage actions by the user like
 * *what payment method is selected
 * *what AWB was searched
 * *what Location was searched
 *
 */
import { Injectable } from '@angular/core';
import { ShipmentInfo } from '@cargos/sprintpay-models';
import { StorageService, StorageTypes } from '@cargos/sprintpay-services';
import { BehaviorSubject, Observable, distinctUntilChanged, map, tap } from 'rxjs';
import { CustomerReferenceFormGroupType } from '../../components/customer-reference-form/models/customer-reference-form';
import { PaymentStatusType } from '../../models/payment-status';
import { SearchType } from '../../models/search-type';
import { NotesForm } from '../../modules/cart/lib-cart/balance/notes/models/notes-form';
import { TermsOfUseForm, notesValidInitialValues, termsOfUseValidInitialValues } from './models/types';

@Injectable({
    providedIn: 'root',
})
export class SummaryService {
    private customerReferenceForm = new BehaviorSubject<CustomerReferenceFormGroupType | null>(null);
    private termsOfUseValid = new BehaviorSubject<TermsOfUseForm>(termsOfUseValidInitialValues);
    private notesValid = new BehaviorSubject<NotesForm>(notesValidInitialValues);
    private mawb: string | null = null;
    private location = new BehaviorSubject<string | null>(null);
    private searchType = new BehaviorSubject<SearchType | null>(null);
    private paymentStatus = new BehaviorSubject<PaymentStatusType | null>(null);
    private pickupDate = new BehaviorSubject<string | null>(null);
    private flightActive = new BehaviorSubject<{
        flightNumber: string;
        flightDate: string;
    } | null>(null);
    private splitShipment = new BehaviorSubject<boolean>(false);
    private costCenter = new BehaviorSubject<string>('');
    private pickupTime = new BehaviorSubject<string | null>(null);
    private firmsCode = '';

    constructor(private storageService: StorageService) {
        const shipment_info: ShipmentInfo = this.storageService.getElement('shipment_info', StorageTypes.SESSION);
        this.setMAWB(shipment_info && shipment_info.awb);
        const location: string | null = this.storageService.getElement('location', StorageTypes.SESSION);
        this.setLocation(location);
        const searchType: SearchType | null = this.storageService.getElement('searchType', StorageTypes.SESSION);
        this.setSearchType(searchType);
    }

    get instant_location_id(): number | null {
        return this.customerReferenceForm?.value?.value?.locationId || null;
    }

    setTermsOfUseValid(termsOfUseForm: TermsOfUseForm | null): void {
        if (termsOfUseForm) {
            this.termsOfUseValid.next(termsOfUseForm);
        } else {
            this.termsOfUseValid.next(termsOfUseValidInitialValues);
        }
    }

    getTermsOfUseValid$(): Observable<TermsOfUseForm> {
        return this.termsOfUseValid.asObservable();
    }

    setMAWB(mawb: string): void {
        this.mawb = mawb;
    }

    getMAWB(): string | null {
        return this.mawb;
    }

    setAndSaveLocationInStorage(location: string | null): void {
        this.setLocation(location);
        this.saveLocation(location);
    }

    private setLocation(location: string | null): void {
        this.location.next(location);
    }

    private saveLocation(location: string | null): void {
        this.storageService.saveElement('location', location, StorageTypes.SESSION);
    }

    getLocation(): string | null {
        return this.location.value;
    }

    getLocation$(): Observable<string | null> {
        return this.location.asObservable().pipe(distinctUntilChanged());
    }

    setAndSaveSearchTypeInStorage(searchType: SearchType | null): void {
        this.setSearchType(searchType);
        this.saveSearchType(searchType);
    }

    private setSearchType(searchType: SearchType | null): void {
        this.searchType.next(searchType);
    }

    private saveSearchType(searchType: SearchType | null): void {
        this.storageService.saveElement('searchType', searchType, StorageTypes.SESSION);
    }

    getSearchType(): string | null {
        return this.searchType.value;
    }

    getSearchType$(): Observable<SearchType | null> {
        return this.searchType.asObservable();
    }

    newCurrentPaymentStatusEvent(event: PaymentStatusType): void {
        this.paymentStatus.next(event);
    }

    onCurrentPaymentStatusEvent$(): Observable<PaymentStatusType | null> {
        return this.paymentStatus.asObservable();
    }

    get instant_payment_status(): PaymentStatusType | null {
        return this.paymentStatus.value;
    }

    get instant_pickup_date(): string | null {
        return this.pickupDate.value;
    }

    setPickUpDate(pickupDate: string): void {
        this.pickupDate.next(pickupDate);
    }

    getPickUpDate(): Observable<string | null> {
        return this.pickupDate.asObservable();
    }

    get instant_pickup_time(): string | null {
        return this.pickupTime.value;
    }

    setPickUpTime(pickupTime: string): void {
        this.pickupTime.next(pickupTime);
    }

    getPickUpTime(): Observable<string | null> {
        return this.pickupTime.asObservable();
    }

    setNotesValid(notesForm: NotesForm | null): void {
        if (notesForm) {
            this.notesValid.next(notesForm);
        } else {
            this.notesValid.next(notesValidInitialValues);
        }
    }

    getNotesValid$(): Observable<NotesForm> {
        return this.notesValid.asObservable();
    }

    setFlightActive(flightNumber: string, flightDate: string): void {
        this.flightActive.next({ flightNumber, flightDate });
    }

    getFlightActive(): Observable<{ flightNumber: string; flightDate: string } | null> {
        return this.flightActive.asObservable();
    }

    get instant_is_split_shipment(): boolean {
        return this.splitShipment.value;
    }

    isSplitShipment$(): Observable<boolean> {
        return this.splitShipment.asObservable();
    }

    setSplitShipmentStatus(isSplit: boolean): void {
        this.splitShipment.next(isSplit);
    }

    get instant_cost_center(): string {
        return this.costCenter.value;
    }

    getCostCenter$(): Observable<string> {
        return this.costCenter.asObservable();
    }

    setCostCenter(costCenter: string): void {
        this.costCenter.next(costCenter);
    }

    /**
     * This firms code is input by the customer when is searching by AWB
     */
    setFirmsCode(firmsCode: string): void {
        this.firmsCode = firmsCode;
    }

    getFirmsCode(): string {
        return this.firmsCode;
    }

    markTermsOfUseAsTouched$(): Observable<string> {
        return this.getTermsOfUseValid$().pipe(
            tap((termsOfUse: TermsOfUseForm) => {
                termsOfUse.termsOfUseFormGroup?.get('termsOfUse')?.markAsTouched();
            }),
            map(() => 'completed')
        );
    }

    markNotesAsTouched$(): Observable<string> {
        return this.getNotesValid$().pipe(
            tap((notes: NotesForm) => {
                notes.notesFormGroup?.get('notes')?.markAsTouched();
            }),
            map(() => 'completed')
        );
    }
}
